import {
  ACCEPTED_FILES_CSV,
  ACCEPTED_FILES_EXCEL,
  ACCEPTED_FILES_JSON,
  ACCEPTED_FILES_MARKDOWN,
  ACCEPTED_FILES_TEXT,
  ACCEPTED_FILES_WORD
} from '@/constants/common';
import {
  TICKET_ATTACHMENT_ACCEPTED_FILE_TYPES,
  TICKET_TYPE
} from '@/pages/Ticket/Ticket.constants';

export const CHANGE_REQUEST_SUBCATEGORIES = [
  {
    id: 1,
    name: 'General'
  },
  {
    id: 2,
    name: 'Internal Networks'
  },
  {
    id: 3,
    name: 'Travel Notification'
  }
];

export const CHANGE_REQUEST_INTERNAL_NETWORKS_TEMPLATE = {
  category: 24,
  subcategory: 2,
  summary: `Hello SOC,\n\nPlease update our Internal Network configuration for our network sensors with the following IP address(es) or IP range(s) as requested below.`,
  team_queue: 'SOC Engineering',
  title: 'Change Internal Networks'
};

export enum CATEGORY_ID {
  CHANGE_REQUEST = 24,
  GENERAL_INQUIRY = 26,
  ESCALATION_REQUEST = 33,
  LOG_RESTORE_EXPORT = 34,
  TUNING_REQUEST = 36,
  VULNERABILITY_MANAGEMENT = 35,
  URGENT = 37,
  KANATI_INVESTIGATE = 38,
  KANATI_ADVISORY = 39,
  KANATI_IR = 40
}

// keep in sync with backend KANATI_MAX_TEXT_LENGTH
export const KANATI_MAX_TEXT_LENGTH = 5000;

export const KANATI_TEXT_TOO_LONG_MESSAGE = `Message to Kanati AI cannot exceed ${KANATI_MAX_TEXT_LENGTH} characters.`;

export const KANATI_ADVISORY_DISCLAIMER =
  'Kanati Advisory is a self-service AI resource for cybersecurity questions and guidance.  AI can make mistakes; validate recommendations before acting.  Human review, consultation, or follow-up work may require a separate advisory engagement.  Contact your account manager to learn more.';

export const KANATI_CATEGORY_INFO_MESSAGES: Record<
  number,
  { title: string; description: string }
> = {
  [CATEGORY_ID.KANATI_ADVISORY]: {
    title: 'Kanati Advisory',
    description:
      'Request a security report, assessment, summary, or recommendations tailored to your organization. Kanati will reply directly on this ticket.'
  },
  [CATEGORY_ID.KANATI_INVESTIGATE]: {
    title: 'Kanati Investigate',
    description:
      'Request an investigation into a security event, user, device, or other concern and receive evidence-based findings. Kanati will reply directly on this ticket.'
  }
};

// keep in sync with backend KANATI_ALLOWED_ATTACHMENT_EXTENSIONS
export const KANATI_ATTACHMENT_ACCEPTED_FILE_TYPES = [
  ...ACCEPTED_FILES_TEXT,
  ...ACCEPTED_FILES_CSV,
  ...ACCEPTED_FILES_MARKDOWN,
  ...ACCEPTED_FILES_JSON,
  ...ACCEPTED_FILES_EXCEL,
  ...ACCEPTED_FILES_WORD
];

export const KANATI_ATTACHMENT_TOOLTIP =
  'Kanati tickets support text, CSV, Markdown, JSON, Excel and Word files';

export const TICKET_ATTACHMENT_TOOLTIP =
  'Please zip unsupported file types to upload';

export const getAttachmentUploaderProps = (isKanati: boolean) => ({
  acceptedTypes: isKanati
    ? KANATI_ATTACHMENT_ACCEPTED_FILE_TYPES
    : TICKET_ATTACHMENT_ACCEPTED_FILE_TYPES,
  titleButtonTooltip: isKanati
    ? KANATI_ATTACHMENT_TOOLTIP
    : TICKET_ATTACHMENT_TOOLTIP
});

const KANATI_ATTACHMENT_EXTENSIONS =
  KANATI_ATTACHMENT_ACCEPTED_FILE_TYPES.filter((type) => type.startsWith('.'));

export const isKanatiAttachmentAllowed = (file: { name?: string }): boolean =>
  KANATI_ATTACHMENT_EXTENSIONS.some((extension) =>
    (file.name ?? '').toLowerCase().endsWith(extension)
  );

export const isKanatiIrCategory = (categoryId: unknown): boolean =>
  categoryId === CATEGORY_ID.KANATI_IR;

export const isKanatiCategory = (categoryId: unknown): boolean =>
  categoryId === CATEGORY_ID.KANATI_INVESTIGATE ||
  categoryId === CATEGORY_ID.KANATI_ADVISORY ||
  isKanatiIrCategory(categoryId);

// Kanati IR can only be selected for incident response (IR) organizations
export const canSelectKanatiCategory = (
  categoryId: unknown,
  isIrOrganization: boolean
): boolean =>
  isKanatiCategory(categoryId) &&
  (!isKanatiIrCategory(categoryId) || isIrOrganization);

export const isKanatiServiceTicket = (
  ticketType: TICKET_TYPE,
  categoryId: unknown
): boolean =>
  ticketType === TICKET_TYPE.SERVICE && isKanatiCategory(categoryId);

export enum SUBCATEGORY_ID {
  INTERNAL_NETWORKS = 2,
  TRAVEL_NOTIFICATION = 3
}

export enum LOGS_ACTION_ID {
  EXPORT = '0',
  RESTORE = '1'
}
